import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { emptyStringValues } from '@/utils/objects';

import {
  TFeaturedPeopleParametersType,
  TFeaturedPeopleType,
  TFeaturedPeopleTypeEnum,
} from '../types';

import { featuredPeopleQueryKeys } from './query-keys';
import { hasImagesToUpload, useUpdatePeopleImages } from './update-people';

export type TPatchFeaturedPeopleParams = {
  id: string;
  name?: string;
  description?: string | null;
  featured_people_type: TFeaturedPeopleTypeEnum;
  parameters?: TFeaturedPeopleParametersType | null;
  main_logo?: File | string;
  logos: File[] | string[];
};

export async function patchFeaturedPeople({
  ...data
}: TPatchFeaturedPeopleParams): Promise<TFeaturedPeopleType> {
  return mediaManagerClient.patch(`/api/v1/peoples/${data.id}`, emptyStringValues(data));
}

type MutationFn = typeof patchFeaturedPeople;

type TusePatchFeaturedPeopleParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchFeaturedPeople({ config }: TusePatchFeaturedPeopleParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: updatePeopleImages } = useUpdatePeopleImages();

  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    onError(error) {
      if (error.response?.status === 409) {
        addErrorNotification(t('Ya existe una persona de interés con ese nombre'));
      } else {
        addErrorNotification(t('No se ha podido actualizar la persona de interés'));
      }
      console.error(error);
    },
    onSuccess(featuredPeoples, variables) {
      addSuccessNotification(t('La persona de interés se ha actualizado correctamente'));
      queryClient.invalidateQueries({ queryKey: featuredPeopleQueryKeys.all });
      if (hasImagesToUpload(variables)) {
        updatePeopleImages({
          id: featuredPeoples.id,
          main_logo: variables.main_logo,
          logos: variables.logos,
        });
      }
    },
    ...config,
    mutationFn: patchFeaturedPeople,
  });
}
